import React, { useState } from 'react';
import cx, { clsx } from 'clsx';
import { useIsMutating } from '@tanstack/react-query';
import { Button, Spinner } from '@printdeal/ui-library';
import { useSelectorMachineContext, useSelectorState } from '@printdeal/product-selector';
import '../../../../css/easy-product-page.css';
import { FormattedMessage, useIntl } from 'react-intl';
import { FeatureToggle } from '@printdeal/feature-management/react';
import { Link as GatsbyLink } from 'gatsby';
import { Money } from '../../../Money';

const ProductDetailEasyFooter = () => {
  const intl = useIntl();
  const isSelectorFetchingPrices = useSelectorMachineContext((state) => state.matches({ STARTED: { 'FETCHING DATA': { PRICES: 'FETCHING PRICES' } } }));
  const isSelectorFetchingServicePrices = useSelectorMachineContext((state) => state.matches({ STARTED: { 'FETCHING DATA': { 'SERVICE PRICES': 'FETCHING SERVICE PRICES' } } }));
  const selectorState = useSelectorState();
  const totalPrice = useSelectorMachineContext((state) => state.context.totalPrice);
  const totalQuantity = useSelectorMachineContext((state) => state.context.totalQuantity);
  const artworkRequired = useSelectorMachineContext((state) => state.context.artworkRequired);
  const merchantRules = useSelectorMachineContext((state) => state.context.merchantRules);
  const { deliveryTool } = merchantRules;
  const [status, setStatus] = useState<'opening-editor' | 'idle'>('idle');
  const isAddingToCart = useIsMutating({ mutationKey: ['addLineItem'] }) > 0;
  const isSelectorIdle = selectorState === 'idle';
  const pricesLoading = isSelectorFetchingPrices || isSelectorFetchingServicePrices;
  // "Checkmatemode: checkmate" and "checkmate: on" will be renamed when MRC is replaced by the business rules app
  const editorEnabled = Boolean(deliveryTool?.checkmate === 'on') || Boolean(deliveryTool?.editor === 'on');
  const defaultShow = Boolean(deliveryTool?.checkmate === undefined) && Boolean(deliveryTool?.editor === undefined);

  const printQMaintenacePages = {
    'nl-NL': '/nl/pages/onderhoud-aan-onze-editor',
    'nl-BE': '/nl/pages/onderhoud-aan-onze-editor',
    'fr-BE': '/fr/pages/service-notre-editeur',
    'fr-FR': '/fr/pages/service-notre-editeur',
  } as Record<string, string>;
  const printQMaintenancePagePath = printQMaintenacePages[intl.locale];

  return (
    <div className={clsx('pd-sticky pd-bottom-0 pd-z-[32] pd-self-end pd-bg-dark pd-text-white')}>
      <div className="pd-flex pd-items-center pd-justify-end pd-p-4 md:pd-p-5" data-hj-allow>
        <FormattedMessage
          id="selector.footer.quantity"
          defaultMessage="{totalQuantity} {totalQuantity, plural, one {piece} other {pieces}} for"
          values={{ totalQuantity }}
        />
        <span className={cx('pd-inline-block pd-mx-2 pd-text-xl pd-font-semibold', { 'pd-animate-pulse': pricesLoading })}>
          {!pricesLoading && totalPrice
            ? (
              <Money
                value={totalPrice.totalNet?.centAmount || 0}
                currency={totalPrice.totalNet?.currencyCode}
              />
            )
            : (<span>—,——</span>)}
        </span>
        <span className="pd-lowercase">
          <FormattedMessage id="general.vat.exclusive" defaultMessage="Excl. VAT" />
        </span>
        <Button
          form="add-product-to-cart"
          type="submit"
          variant={artworkRequired ? 'secondary' : 'primary'}
          className="pd-ml-3"
          disabled={isAddingToCart || !isSelectorIdle}
          iconBefore={(isAddingToCart)
            ? (
              <Spinner
                variant="primary"
                className="pd-ml-3"
                screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
              />
            )
            : undefined}
        >
          <FormattedMessage id="product.button.addToCart" defaultMessage="Add to cart" />
        </Button>
        {artworkRequired && (editorEnabled || defaultShow) && (
          <FeatureToggle id="printq_in_maintenance">
            <FeatureToggle.Disabled>
              <Button
                className="pd-ml-3"
                disabled={!isSelectorIdle}
                form="fileUploadButton"
                type="submit"
                variant="primary"
                data-ft-element="fileUploadButton"
                onClick={() => setStatus('opening-editor')}
              >
                {status === 'opening-editor'
                && (
                <Spinner
                  variant="white"
                  className="pd-mr-3"
                  screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
                />
                )}
                <FormattedMessage id="selector.open.editor" defaultMessage="Start with designing" />
              </Button>
            </FeatureToggle.Disabled>
            <FeatureToggle.Enabled>
              {
              printQMaintenancePagePath && (
                <Button
                  as={GatsbyLink}
                  to={printQMaintenancePagePath}
                  className="pd-ml-3"
                  disabled={!isSelectorIdle}
                  type="submit"
                  variant="primary"
                  data-ft-element="fileUploadButton"
                >
                  <FormattedMessage id="selector.open.editor" defaultMessage="Start with designing" />
                </Button>
              )
            }
            </FeatureToggle.Enabled>
          </FeatureToggle>
        )}
      </div>
    </div>
  );
};

export default ProductDetailEasyFooter;
